<template>
  <div class="calc-page">
    <!-- Header -->
    <div class="calc-header">
      <div>
        <span class="header-eyebrow">Produccion</span>
        <h2 class="header-title">Calculadora</h2>
      </div>
      <div class="header-actions">
        <button class="btn-calc btn-calc--ghost" @click="limpiarCalcular()">
          <i class="fa-solid fa-eraser"></i> Limpiar
        </button>
        <button class="btn-calc btn-calc--green" @click="exportarListado()">
          <i class="fa-solid fa-download"></i> Descargar listado
        </button>
      </div>
    </div>

    <!-- Materiales KPIs -->
    <div class="section-label">
      <i class="fa-solid fa-boxes-stacked"></i> Materiales
    </div>
    <div class="kpi-strip kpi-strip--6">
      <div class="kpi-card" v-for="item in statisticsItems" :key="'m-'+item.subtitle">
        <span class="kpi-label">{{ item.subtitle }}</span>
        <span class="kpi-value">{{ item.title }}</span>
      </div>
    </div>

    <!-- Materiales list toggle -->
    <div class="toggle-section">
      <button class="btn-toggle" @click="mostrandoListado = !mostrandoListado">
        <i :class="mostrandoListado ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
        {{ mostrandoListado ? 'Ocultar materiales' : 'Ver listado materiales' }}
      </button>
      <button v-if="mostrandoListado" class="btn-copy" @click="copyTableExcel()" title="Copiar a Excel">
        <i class="fa-solid fa-copy"></i>
      </button>
    </div>

    <transition name="slide">
      <div class="list-card" v-if="mostrandoListado">
        <div class="table-wrapper">
          <b-table
            :items="listMaterialesPedir"
            :fields="columnasMateriales"
            class="mb-0"
            hover
            sort-icon-left
            responsive
            show-empty
            empty-text="Sin materiales calculados"
            thead-class="table-head"
            tbody-tr-class="table-row"
            small
          >
            <template #cell(cantidad)="data">
              <span class="cell-mono">{{ parseDecimalString(data.item.cantidad) }}</span>
            </template>
            <template #cell(pedir)="data">
              <span class="cell-mono" v-if="data.item.pedir != null">{{ parseDecimalString(data.item.pedir) }}</span>
            </template>
            <template #cell(precioTotal)="data">
              <span class="cell-mono cell-mono--accent">{{ data.item.precioTotal }}</span>
            </template>
          </b-table>
        </div>
      </div>
    </transition>

    <!-- Tiempos KPIs -->
    <div class="section-label" style="margin-top: 20px;">
      <i class="fa-solid fa-clock"></i> Tiempos de fabricacion
    </div>
    <div class="kpi-strip kpi-strip--6">
      <div class="kpi-card" v-for="item in statisticsItemsTiempos" :key="'t-'+item.subtitle">
        <span class="kpi-label">{{ item.subtitle }}</span>
        <span class="kpi-value">{{ item.title }}</span>
      </div>
    </div>

    <!-- Tiempos list toggle -->
    <div class="toggle-section">
      <button class="btn-toggle" @click="mostrandoListadoTiempos = !mostrandoListadoTiempos">
        <i :class="mostrandoListadoTiempos ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
        {{ mostrandoListadoTiempos ? 'Ocultar tiempos' : 'Ver listado tiempos' }}
      </button>
    </div>

    <transition name="slide">
      <div class="list-card" v-if="mostrandoListadoTiempos">
        <div class="table-wrapper">
          <b-table
            :items="listadoTiemposSku"
            :fields="columnasTiempos"
            class="mb-0"
            hover
            sort-icon-left
            responsive
            show-empty
            empty-text="Sin tiempos calculados"
            thead-class="table-head"
            tbody-tr-class="table-row"
            small
          />
        </div>
      </div>
    </transition>

    <!-- Total KPIs -->
    <div class="section-label" style="margin-top: 20px;">
      <i class="fa-solid fa-calculator"></i> Resumen total
    </div>
    <div class="kpi-strip kpi-strip--5">
      <div class="kpi-card" v-for="item in statisticsTotal" :key="'s-'+item.subtitle">
        <span class="kpi-label">{{ item.subtitle }}</span>
        <span class="kpi-value" :class="{ 'kpi-value--accent': item.subtitle === 'TOTAL', 'kpi-value--green': item.subtitle === 'Beneficio' }">{{ item.title }}</span>
      </div>
    </div>

    <!-- Controls -->
    <div class="controls-bar">
      <div class="control-group">
        <label class="control-label">Localizacion</label>
        <v-select v-model="localizacionSelect" :options="listadoLocalizacion" class="select-styled" />
      </div>

      <div class="control-group">
        <label class="control-label">Pedido</label>
        <div class="radio-pills">
          <button :class="['radio-pill', { active: radioPedido === 'Ninguno' }]" @click="radioPedido = 'Ninguno'">Ninguno</button>
          <button :class="['radio-pill', { active: radioPedido === 'Pedido Actual' }]" @click="radioPedido = 'Pedido Actual'">Actual</button>
          <button :class="['radio-pill', { active: radioPedido === 'Pedido Anual' }]" @click="radioPedido = 'Pedido Anual'">Anual</button>
        </div>
      </div>

      <div class="control-group">
        <label class="control-label">Opciones</label>
        <div class="toggle-row">
          <button :class="['toggle-chip', { active: checkCalcular }]" @click="checkCalcular = !checkCalcular">
            <i :class="checkCalcular ? 'fa-solid fa-check' : 'fa-solid fa-xmark'"></i> Calcular
          </button>
          <button :class="['toggle-chip', { active: checkStock }]" @click="checkStock = !checkStock">
            <i :class="checkStock ? 'fa-solid fa-check' : 'fa-solid fa-xmark'"></i> Usar stock
          </button>
        </div>
      </div>
    </div>

    <!-- SKU Table -->
    <div class="list-card">
      <div class="list-card__header">
        <h3 class="list-card__title">Articulos</h3>
        <div class="list-card__filters">
          <div class="search-box">
            <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
            <input type="text" class="search-box__input" placeholder="Buscar SKU..." v-model="buscar" />
          </div>
          <button class="btn-copy" @click="copyTableSku()" title="Copiar SKUs">
            <i class="fa-solid fa-copy"></i> Copiar
          </button>
        </div>
      </div>

      <div class="table-wrapper">
        <b-table
          :items="articulosBusqueda"
          :fields="columnasAll"
          class="mb-0"
          hover
          sort-icon-left
          responsive
          ref="table"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(sku)="data">
            <span class="cell-sku">{{ data.item.sku }}</span>
          </template>
          <template #cell(calcular)="data">
            <input
              type="number"
              class="calc-input"
              v-model="data.item.calcular"
              @wheel.prevent
              @focus="togglePicker(data)"
              placeholder="0"
            />
          </template>
          <template #cell(pedidoActual)="data">
            <span class="cell-mono" v-if="data.item.pedidoActual">{{ data.item.pedidoActual }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
          <template #cell(pedidoAnual)="data">
            <span class="cell-mono" v-if="data.item.pedidoAnual">{{ data.item.pedidoAnual }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
          <template #cell(stock)="data">
            <span class="cell-mono" v-if="data.item.stock">{{ data.item.stock }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'

  export default {
    components: {
        vSelect,
    },
    data () {
      return {
        radioPedido: 'Ninguno',
        statisticsItems: [
          { icon: 'fa-solid fa-users', color: 'light-warning', title: '0', subtitle: 'Proveedores' },
          { icon: 'fa-solid fa-barcode', color: 'light-primary', title: '0', subtitle: 'Sku' },
          { icon: 'fa-solid fa-boxes-stacked', color: 'light-info', title: '0', subtitle: 'Productos' },
          { icon: 'fa-solid fa-box', color: 'light-success', title: '0', subtitle: 'Materiales' },
          { icon: 'fa-solid fa-euro-sign', color: 'light-danger', title: '0', subtitle: 'Coste' },
          { icon: 'fa-solid fa-coins', color: 'light-warning', title: '0', subtitle: 'Coste "Minimo"' },
        ],
        statisticsItemsTiempos: [
          { icon: 'fa-solid fa-scissors', color: 'light-info', title: '0', subtitle: 'Corte' },
          { icon: 'fa-solid fa-hands-praying', color: 'light-warning', title: '0', subtitle: 'Preparado' },
          { icon: 'fa-solid fa-timeline', color: 'light-primary', title: '0', subtitle: 'Confeccion' },
          { icon: 'fa-brands fa-fedex', color: 'light-success', title: '0', subtitle: 'Shipping' },
          { icon: 'fa-solid fa-box', color: 'light-danger', title: '0', subtitle: 'Packaging' },
          { icon: 'fa-solid fa-tree-city', color: 'light-warning', title: '0', subtitle: 'Ubrique' },
        ],
        statisticsTotal: [
          { icon: 'fa-solid fa-boxes-stacked', color: 'light-info', title: '0', subtitle: 'Materiales' },
          { icon: 'fa-solid fa-hands-praying', color: 'light-warning', title: '0', subtitle: 'Fabricacion' },
          { icon: 'fa-brands fa-fedex', color: 'light-success', title: '0', subtitle: 'Shipping' },
          { icon: 'fa-solid fa-euro-sign', color: 'light-danger', title: '0', subtitle: 'TOTAL' },
          { icon: 'fa-solid fa-percent', color: 'light-primary', title: '0', subtitle: 'Beneficio' },
        ],
        columnasAll: [
          { key: "sku", label: "SKU", sortable: true },
          { key: "calcular", label: "Calcular", sortable: false, thStyle: { width: '80px' } },
          { key: "pedidoActual", label: "P. Actual", sortable: true },
          { key: "pedidoAnual", label: "P. Anual", sortable: true },
          { key: "stock", label: "Stock", sortable: true },
        ],
        columnasMateriales: [
          { key: "nombre", label: "Nombre", sortable: true },
          { key: "color", label: "Color", sortable: true },
          { key: "categoria", label: "Categoria", sortable: true },
          { key: "proveedor", label: "Proveedor", sortable: true },
          { key: "cantidad", label: "Cantidad", sortable: true },
          { key: "pedir", label: "Pedir", sortable: true },
          { key: "precioTotal", label: "Precio", sortable: true },
        ],
        columnasTiempos: [
          { key: "sku", label: "Sku", sortable: true },
          { key: "cantidad", label: "Cantidad", sortable: true },
          { key: "corte", label: "Corte", sortable: true },
          { key: "preparado", label: "Preparado", sortable: true },
          { key: "confeccion", label: "Confeccion", sortable: true },
          { key: "shipping", label: "Shipping", sortable: true },
          { key: "packaging", label: "Packaging", sortable: true },
          { key: "ubrique", label: "Ubrique", sortable: true },
          { key: "total", label: "TOTAL", sortable: true },
        ],
        articulos: [],
        articulosBusqueda: [],
        buscar: "",
        listadoMostrar: ["Todos", "Pedido Actual", "Pedido Anual"],
        listadoLocalizacion: ["Todos", "Elche", "Ubrique"],
        localizacionSelect: "Todos",
        mostrarSelect: "Todos",
        itemSelect: {pedido: undefined},
        listAllSkuMaterial: [],
        listMateriales: [],
        checkCalcular: true,
        checkStock: false,
        mostrandoListado: false,
        mostrandoListadoTiempos: false,
        listMaterialesPedir: [],
        listArticulosPedidos: [],
        articulosTiempos: [],
        precioTiempos: [],
        listadoTiemposSku: [],
        libra: "1.138762",
      }
    },
    methods:{
      loadItems(){
        axios.post("/uso", {seccion:"calculadora"});
        axios
            .get("/articulos/variants")
            .then(response => { return response.data; })
            .then(resArticulos => {
                this.articulos= resArticulos.map((x) => {
                    return {
                        ...x,
                        calcular: '',
                        pedidoActual: this.parseUndefined(x.pedidoActual),
                        pedidoAnual: this.parseUndefined(x.pedidoAnual),
                    };
                });
                this.articulosBusqueda = this.articulos;
            });

        axios.get("/materiales", { params: { sinImg: 1 } }).then(response => { return response.data; }).then(res => { this.listMateriales = res; });
        axios.get("/materiales/sku").then(response => { return response.data; }).then(res => { this.listAllSkuMaterial = res; });
        axios.get("/tiempos/variants").then(response => { return response.data; }).then(resArticulos => { this.articulosTiempos = resArticulos; });
        axios.get("/tiempos/precios").then(response => { return response.data; }).then(resPrecios => { this.precioTiempos = resPrecios; });
      },
      parseUndefined(data){
        if(data == undefined || data == "undefined") return '';
        return data;
      },
      parseDecimalString(data){
        if(String(data).includes(",") || String(data).includes("."))
          return data.toFixed(2)
        else
          return data
      },
      buscarFuncion(){
        this.articulosBusqueda = [];
        if (this.buscar === "") {
          this.articulosBusqueda = [...this.articulos];
        } else {
          this.articulosBusqueda = this.articulos.filter(articulo => {
            return articulo.sku.toLowerCase().includes(this.buscar.toLowerCase());
          });
        }
      },
      togglePicker(data){ this.itemSelect = data.item; },
      calcularMateriales(){
        let sku = 0;
        let coste = 0;
        let productosTotal = 0;
        let costeMinimo = 0;
        let listMaterialesNecesarios = [];
        this.listMaterialesPedir = [];
        this.listArticulosPedidos = [];

        this.articulos.forEach(articulo => {
          let newArticulo = {sku: articulo.sku, cantidad: 0};
          let checkLocalizacion = false;
          if(this.localizacionSelect == "Todos") checkLocalizacion = true;
          else {
            if(this.localizacionSelect == "Elche" && !articulo.localizacion) checkLocalizacion = true;
            if(this.localizacionSelect == "Ubrique" && articulo.localizacion) checkLocalizacion = true;
          }

          if(checkLocalizacion) {
            let sumSku = false;
            let serie = this.listAllSkuMaterial.filter(function (el) { return el.sku == articulo.sku; });

            if(articulo.calcular > 0 && this.checkCalcular) {
              sumSku = true;
              productosTotal += parseInt(articulo.calcular);
              newArticulo.cantidad += parseInt(articulo.calcular);
              serie.forEach(element => { listMaterialesNecesarios.push({...element, aux: element.cantidad * articulo.calcular}); });
            }

            if(this.radioPedido == "Pedido Anual" && articulo.pedidoAnual > 0) {
              sumSku = true;
              productosTotal += parseInt(articulo.pedidoAnual);
              newArticulo.cantidad += parseInt(articulo.pedidoAnual);
              serie.forEach(element => { listMaterialesNecesarios.push({...element, aux: element.cantidad * articulo.pedidoAnual}); });
            }

            if(this.radioPedido == "Pedido Actual" && articulo.pedidoActual > 0) {
              sumSku = true;
              productosTotal += parseInt(articulo.pedidoActual);
              newArticulo.cantidad += parseInt(articulo.pedidoActual);
              serie.forEach(element => { listMaterialesNecesarios.push({...element, aux: element.cantidad * articulo.pedidoActual}); });
            }

            if(sumSku) { sku++; this.listArticulosPedidos.push(newArticulo); }
          }
        });

        listMaterialesNecesarios.forEach(data => {
          let indexMaterial = this.listMateriales.findIndex(x => x.id === data.idMaterial);
          let precioMaterial = parseFloat(this.listMateriales[indexMaterial].precio.replace(',','.'));
          if(isNaN(precioMaterial)) precioMaterial = 0;

          let cantidadSkuMaterial = data.aux;
          if(this.listMateriales[indexMaterial].tipoMedidas.toLowerCase().includes("cuadrado"))
            cantidadSkuMaterial = cantidadSkuMaterial* 1.15;

          let index = this.listMaterialesPedir.findIndex(x => x.id === data.idMaterial);
          if (index == -1) {
            this.listMaterialesPedir.push({...this.listMateriales[indexMaterial], precioTotal: precioMaterial * cantidadSkuMaterial, cantidad: cantidadSkuMaterial});
          } else {
            this.listMaterialesPedir[index].precioTotal += precioMaterial * cantidadSkuMaterial;
            this.listMaterialesPedir[index].cantidad += cantidadSkuMaterial;
          }
        });

        let listProveedores = [];
        this.listMaterialesPedir.forEach(data => {
          let index = listProveedores.findIndex(x => x === data.proveedor);
          if (index == -1) listProveedores.push(data.proveedor);
        });

        if(this.checkStock) {
          this.listMaterialesPedir.forEach(data => {
            let stock = 0;
            let precioMaterial = parseFloat(data.precio.replace(',','.'));
            if(data.stock != "") stock = data.stock;
            let cantidadMaterialTotal = data.medidas * stock;
            let cantidadPedir = data.cantidad - cantidadMaterialTotal;
            if(cantidadPedir < 0) cantidadPedir = 0;
            data.pedir = cantidadPedir;
            data.precioTotal = precioMaterial * cantidadPedir;
          });

          let index = this.columnasMateriales.findIndex(x => x.key === "pedir");
          if (index == -1) {
            this.columnasMateriales.splice(this.columnasMateriales.length-1, 0, {key: "pedir",label: "Pedir",sortable: true,});
          }
        } else {
          let index = this.columnasMateriales.findIndex(x => x.key === "pedir");
          if (index != -1) this.columnasMateriales.splice(index, 1);
          this.listMaterialesPedir.forEach(data => { data.pedir = data.cantidad; });
        }

        this.listMaterialesPedir.forEach(data => {
          coste += data.precioTotal;
          data.precioTotal = data.precioTotal.toFixed(2);
          let aux1 = data.pedir / parseFloat(data.medidas.replace(',','.'));
          aux1 = Math.ceil(aux1);
          if(!isNaN(parseFloat(data.precio.replace(',','.')))) {
            costeMinimo += aux1 * (parseFloat(data.precio.replace(',','.')) * parseFloat(data.medidas.replace(',','.')));
          }
        });

        this.statisticsItems[0].title = listProveedores.length;
        this.statisticsItems[1].title = sku;
        this.statisticsItems[2].title = productosTotal;
        this.statisticsItems[3].title = this.listMaterialesPedir.length;
        this.statisticsItems[4].title = this.parseDecimalString(coste);
        this.statisticsItems[5].title = this.parseDecimalString(costeMinimo);

        this.calcularPrecioFabricacion(coste);
      },
      calcularPrecioFabricacion(totalMateriales) {
        this.listadoTiemposSku = [];
        let totalFabricacion = 0;
        let totalShipping = 0;
        let totalTotal = 0;
        let corte = 0, preparado = 0, confeccion = 0, shipping = 0, packagin = 0, ubrique = 0;
        let precioVentaTotal = 0;

        let precioCorte = this.precioTiempos.find((e) => e.trabajo === "Corte");
        let precioPreparado = this.precioTiempos.find((e) => e.trabajo === "Preparado");
        let precioConfeccion = this.precioTiempos.find((e) => e.trabajo === "Confección");

        this.listArticulosPedidos.forEach(articulo => {
          let articuloFind = this.articulosTiempos.find((e) => e.sku === articulo.sku);
          let articuloPrecioVenta = this.articulos.find((e) => e.sku === articulo.sku);

          if(articuloFind) {
            let cA=0, pA=0, coA=0, sA=0, pkA=0, uA=0;
            if(articuloFind.corte != "") cA = parseFloat(articuloFind.corte) * (precioCorte.precio/60) * articulo.cantidad;
            if(articuloFind.preparado != "") pA = parseFloat(articuloFind.preparado) * (precioPreparado.precio/60) * articulo.cantidad;
            if(articuloFind.confeccion != "") coA = parseFloat(articuloFind.confeccion) * (precioConfeccion.precio/60) * articulo.cantidad;
            if(articuloFind.shipping != "") sA = parseFloat(articuloFind.shipping) * articulo.cantidad;
            if(articuloFind.packaging != "") pkA = parseFloat(articuloFind.packaging) * articulo.cantidad;
            if(articuloFind.precioTotalUbrique != "") uA = parseFloat(articuloFind.precioTotalUbrique) * articulo.cantidad;

            corte += cA; preparado += pA; confeccion += coA; shipping += sA; packagin += pkA; ubrique += uA;
            totalFabricacion += cA + pA + coA + uA;
            totalShipping += sA + pkA;

            this.listadoTiemposSku.push({
              sku: articuloFind.sku, cantidad: articulo.cantidad,
              corte: this.parseDecimalString(cA), preparado: this.parseDecimalString(pA),
              confeccion: this.parseDecimalString(coA), shipping: this.parseDecimalString(sA),
              packaging: this.parseDecimalString(pkA), ubrique: this.parseDecimalString(uA),
              total: this.parseDecimalString(cA+pA+coA+sA+pkA+uA)
            });
          }

          if(articuloPrecioVenta) precioVentaTotal += (articuloPrecioVenta.precio * this.libra) * articulo.cantidad;
        });

        this.statisticsItemsTiempos[0].title = this.parseDecimalString(corte);
        this.statisticsItemsTiempos[1].title = this.parseDecimalString(preparado);
        this.statisticsItemsTiempos[2].title = this.parseDecimalString(confeccion);
        this.statisticsItemsTiempos[3].title = this.parseDecimalString(shipping);
        this.statisticsItemsTiempos[4].title = this.parseDecimalString(packagin);
        this.statisticsItemsTiempos[5].title = this.parseDecimalString(ubrique);

        totalTotal += totalFabricacion + totalMateriales + totalShipping;
        let porcentaje = (precioVentaTotal-totalTotal)/precioVentaTotal * 100;
        if(isNaN(porcentaje)) porcentaje = 0;

        this.statisticsTotal[0].title = this.parseDecimalString(totalMateriales);
        this.statisticsTotal[1].title = this.parseDecimalString(totalFabricacion);
        this.statisticsTotal[2].title = this.parseDecimalString(totalShipping);
        this.statisticsTotal[3].title = this.parseDecimalString(totalTotal);
        this.statisticsTotal[4].title = this.parseDecimalString(porcentaje) + " %";
      },
      exportarListado(){
        let precioCorte = this.precioTiempos.find((e) => e.trabajo === "Corte");
        let precioPreparado = this.precioTiempos.find((e) => e.trabajo === "Preparado");
        let precioConfeccion = this.precioTiempos.find((e) => e.trabajo === "Confección");
        let listado = JSON.parse(JSON.stringify(this.articulos));

        listado.forEach(articulo => {
          articulo.materialesPrecio = 0; articulo.confeccionPrecio = 0;
          articulo.packagingPrecio = 0; articulo.shippingPrecio = 0;
          articulo.totalPreparacion = 0;
          articulo.precioEuro = articulo.precio * this.libra;
          articulo.margen = 0;

          let serie = this.listAllSkuMaterial.filter(function (el) { return el.sku == articulo.sku; });
          serie.forEach(data => {
            let indexMaterial = this.listMateriales.findIndex(x => x.id === data.idMaterial);
            let precioMaterial = parseFloat(this.listMateriales[indexMaterial].precio.replace(',','.'));
            if(isNaN(precioMaterial)) precioMaterial = 0;
            let cantidadSkuMaterial = parseFloat(data.cantidad);
            if(this.listMateriales[indexMaterial].tipoMedidas.toLowerCase().includes("cuadrado"))
              cantidadSkuMaterial = cantidadSkuMaterial* 1.15;
            articulo.materialesPrecio += precioMaterial * cantidadSkuMaterial;
          });
          articulo.materialesPrecio = articulo.materialesPrecio.toFixed(2);

          let articuloFind = this.articulosTiempos.find((e) => e.sku === articulo.sku);
          if(articuloFind) {
            let cA=0, pA=0, coA=0, sA=0, pkA=0, uA=0;
            if(articuloFind.corte != "") cA = parseFloat(articuloFind.corte) * (precioCorte.precio/60);
            if(articuloFind.preparado != "") pA = parseFloat(articuloFind.preparado) * (precioPreparado.precio/60);
            if(articuloFind.confeccion != "") coA = parseFloat(articuloFind.confeccion) * (precioConfeccion.precio/60);
            if(articuloFind.shipping != "") sA = parseFloat(articuloFind.shipping);
            if(articuloFind.packaging != "") pkA = parseFloat(articuloFind.packaging);
            if(articuloFind.precioTotalUbrique != "") uA = parseFloat(articuloFind.precioTotalUbrique);
            articulo.confeccionPrecio = (cA + pA + coA + uA).toFixed(2);
            articulo.packagingPrecio = pkA;
            articulo.shippingPrecio = sA;
          }

          articulo.totalPreparacion = (parseFloat(articulo.materialesPrecio) + parseFloat(articulo.confeccionPrecio)).toFixed(2);
          let total = parseFloat(articulo.materialesPrecio) + parseFloat(articulo.confeccionPrecio) + parseFloat(articulo.packagingPrecio) + parseFloat(articulo.shippingPrecio);
          articulo.margen = ((articulo.precioEuro - total) / articulo.precioEuro * 100).toFixed(2);
        });

        var csv = "SKU;Confeccion;Materias;Total;Packaging;Shipping;RRP E;RRP L;ROAS %\n";
        listado.forEach(data => {
          csv += data.sku+";"+data.confeccionPrecio+";"+data.materialesPrecio+";"+data.totalPreparacion+";"+data.packagingPrecio+";"+data.shippingPrecio+";"+data.precioEuro.toFixed(2)+";"+data.precio+";"+data.margen+"\n";
        });

        navigator.clipboard.writeText(csv);
        this.$bvToast.toast(`Se han copiado ${listado.length} articulos en tu portapapeles`, { title: 'Articulos', variant: 'success', solid: true })
      },
      limpiarCalcular() {
        this.articulos.forEach(data => { data.calcular = ""; });
        this.articulosBusqueda.forEach(data => { data.calcular = ""; });
      },
      copyTableExcel(){
        var csv = "";
        if(this.checkStock)
          csv = "Nombre;Color;Categoria;Proveedor;Ref Proveedor;Cantidad;Pedir;Precio\n";
        else
          csv = "Nombre;Color;Categoria;Proveedor;Ref Proveedor;Cantidad;Precio\n";

        this.listMaterialesPedir.forEach(data => {
          if(this.checkStock)
            csv += data.nombre+";"+data.color+";"+data.categoria+";"+data.proveedor+";"+data.ref+";"+String(data.cantidad).replace('.',',')+";"+String(data.pedir).replace('.',',')+";"+data.precio+"\n";
          else
            csv += data.nombre+";"+data.color+";"+data.categoria+";"+data.proveedor+";"+data.ref+";"+String(data.cantidad).replace('.',',')+";"+data.precio+"\n";
        });
        navigator.clipboard.writeText(csv);
      },
      copyTableSku(){
        var csv = "Sku;Cantidad\n";
        this.listArticulosPedidos.forEach(data => { csv += data.sku+";"+data.cantidad+"\n"; });
        navigator.clipboard.writeText(csv);
        this.$bvToast.toast(`Se han copiado ${this.listArticulosPedidos.length} sku en tu portapapeles`, { title: 'Sku', variant: 'success', solid: true })
      },
    },
    mounted() { this.loadItems(); },
    watch: {
      buscar(){ this.buscarFuncion(); },
      "itemSelect.calcular": function (newVal, oldVal){
        if (this.itemSelect.calcular != undefined) {
          let index = this.articulos.findIndex(x => x.sku === this.itemSelect.sku);
          if (index != -1) this.articulos[index].calcular = this.itemSelect.calcular;
          let indexB = this.articulosBusqueda.findIndex(x => x.sku === this.itemSelect.sku);
          if (indexB != -1) this.articulosBusqueda[indexB].calcular = this.itemSelect.calcular;
          this.calcularMateriales();
        }
      },
      checkCalcular(){ this.calcularMateriales(); },
      radioPedido(){ this.calcularMateriales(); },
      checkStock(){ this.calcularMateriales(); },
      localizacionSelect(){ this.calcularMateriales(); }
    }
  }
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$green: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.calc-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

.calc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.header-actions { display: flex; gap: 8px; }

// Buttons
.btn-calc {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: $radius-sm;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--green { background: $green; color: white; &:hover { background: darken($green, 8%); } }
  &--ghost { background: transparent; color: $text-muted; border: 1px solid $border; &:hover { background: $bg; color: $text; } }
}

// Section label
.section-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  i { font-size: 12px; color: $primary; }
}

// KPI Strip
.kpi-strip {
  display: grid;
  gap: 10px;
  margin-bottom: 14px;

  &--5 { grid-template-columns: repeat(5, 1fr); }
  &--6 { grid-template-columns: repeat(6, 1fr); }
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 18px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin-top: 4px;

  &--accent { color: $danger; }
  &--green { color: $green; }
}

// Toggle section
.toggle-section {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.btn-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { border-color: $primary; color: $primary; }
  i { font-size: 10px; }
}

.btn-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { border-color: $primary; color: $primary; background: rgba($primary, 0.04); }
}

// Controls bar
.controls-bar {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  padding: 16px 20px;
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.control-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $text-muted;
  margin: 0;
}

.radio-pills {
  display: flex;
  gap: 4px;
}

.radio-pill {
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { border-color: $primary; color: $primary; }
  &.active { background: $primary; border-color: $primary; color: white; }
}

.toggle-row { display: flex; gap: 6px; }

.toggle-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &.active { background: $primary; border-color: $primary; color: white; }
  &:hover:not(.active) { border-color: $primary; color: $primary; }
  i { font-size: 10px; }
}

// List Card
.list-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;
  margin-bottom: 20px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 22px;
    border-bottom: 1px solid $border;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: $text;
    margin: 0;
  }

  &__filters {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.search-box {
  position: relative;
  width: 220px;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-muted;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;

    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

.table-wrapper { padding: 0; }

// Los ::v-deep van bajo .calc-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.calc-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 8px 14px;
  background: transparent;
}

.calc-page ::v-deep .table-row td {
  padding: 8px 14px;
  vertical-align: middle;
  border-color: lighten($border, 3%);
  font-size: 13px;
}

.calc-page ::v-deep .table-row:hover td { background: $bg; }

.cell-sku {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  color: $text;
}

.cell-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text;
  font-feature-settings: "tnum";

  &--accent { color: $primary; font-weight: 600; }
}

.cell-muted { color: #CBD5E1; font-size: 12px; }

.calc-input {
  width: 60px;
  padding: 4px 8px;
  border: 1px solid $border;
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  text-align: center;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; }
  &:focus { outline: none; border-color: $primary; box-shadow: 0 0 0 3px rgba($primary, 0.1); }
  &::placeholder { color: #CBD5E1; font-weight: 400; }
}

// Slide transition
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
  max-height: 600px;
  overflow: hidden;
}
.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
}

// Responsive
@media (max-width: 992px) {
  .kpi-strip--6 { grid-template-columns: repeat(3, 1fr); }
  .kpi-strip--5 { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 768px) {
  .kpi-strip--6, .kpi-strip--5 { grid-template-columns: repeat(2, 1fr); }
  .calc-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .controls-bar { flex-direction: column; align-items: flex-start; }
}
</style>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

.calc-page {
  .select-styled {
    min-width: 150px;

    .vs__dropdown-toggle {
      min-height: 33px;
      border-radius: 8px;
      border: 1px solid #E2E8F0;
    }

    .vs__search {
      font-size: 13px;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    &.vs--open .vs__dropdown-toggle {
      border-color: #3B82F6;
      box-shadow: 0 0 0 3px rgba(#3B82F6, 0.1);
    }
  }
}

// Dark mode
.dark-layout {
  .calc-page {
    .header-title { color: #E2E8F0; }

    .kpi-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }

    .section-label { color: #94A3B8; }

    .btn-toggle, .btn-copy { border-color: #475569; color: #94A3B8; &:hover { border-color: #3B82F6; color: #3B82F6; } }
    .btn-calc--ghost { border-color: #475569; color: #94A3B8; &:hover { background: #334155; color: #E2E8F0; } }

    .controls-bar { background: #1E293B; border-color: #334155; }
    .radio-pill { border-color: #475569; color: #94A3B8; &:hover { border-color: #3B82F6; color: #3B82F6; } &.active { background: #3B82F6; border-color: #3B82F6; color: white; } }
    .toggle-chip { border-color: #475569; color: #94A3B8; &.active { background: #3B82F6; border-color: #3B82F6; color: white; } &:hover:not(.active) { border-color: #3B82F6; color: #3B82F6; } }

    .list-card { background: #1E293B; border-color: #334155; }
    .list-card__header { border-color: #334155; }
    .list-card__title { color: #E2E8F0; }

    .search-box__input { border-color: #334155; color: #E2E8F0; &::placeholder { color: #475569; } &:focus { border-color: #3B82F6; } }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .table-row:hover td { background: rgba(255,255,255,0.03); }

    .cell-sku { color: #E2E8F0; }
    .cell-mono { color: #E2E8F0; }

    .calc-input { background: #0F172A; border-color: #334155; color: #60A5FA; &:focus { border-color: #3B82F6; } }

    .select-styled .vs__dropdown-toggle { border-color: #334155; background: #0F172A; }
    .select-styled .vs__search { color: #E2E8F0; }
  }
}
</style>
